/**
 * i18n tối giản — docs/08 §8.8: "chuẩn bị i18n (vi, en) ngay từ đầu,
 * không hard-code chuỗi trong component".
 *
 * Ngôn ngữ mặc định là `vi`. Thiếu khoá ở `en` sẽ rơi về `vi` để không
 * bao giờ hiện khoá thô cho người dùng.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { vi } from './vi';
import { en } from './en';

export type Locale = 'vi' | 'en';
export type Dict = Record<string, string>;

const DICTS: Record<Locale, Dict> = { vi, en };
const STORAGE_KEY = 'dnshub.locale';

interface I18nValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function readLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'vi' || stored === 'en') return stored;
  } catch {
    /* localStorage bị chặn — dùng mặc định */
  }
  return 'vi';
}

function dichKhoa(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const raw = DICTS[locale][key] ?? DICTS.vi[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_m, name: string) => String(vars[name] ?? `{${name}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* bỏ qua */
    }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => dichKhoa(locale, key, vars),
    [locale],
  );

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n phải nằm trong <I18nProvider>');
  return ctx;
}

/** Rút gọn khi chỉ cần hàm dịch. */
export function useT(): I18nValue['t'] {
  return useI18n().t;
}

/**
 * Ngôn ngữ đang chọn, an toàn cả khi thiếu provider.
 *
 * `format.ts` cần giá trị này để chọn locale cho `Intl`, và nó bị gọi từ những
 * chỗ sâu trong cây lẫn từ test render trần — cùng lý do với `useTLa()` bên
 * dưới, nên cũng rơi về `vi` thay vì ném.
 */
export function useLocale(): Locale {
  return useContext(I18nContext)?.locale ?? 'vi';
}

/**
 * Dịch từ ngoài cây React — chỉ dành cho `api.ts`.
 *
 * `api.ts` là module thuần: nó dựng `ApiError` ở tầng mạng, nơi không có
 * component nào để treo hook. Trước OQ-290 nó nhét thẳng tiếng Việt vào
 * `message`, nên hai thông điệp mà người dùng gặp đúng lúc hoang mang nhất —
 * mất mạng và lỗi không tên — là hai chuỗi duy nhất không bao giờ đổi ngôn ngữ.
 *
 * Đọc `localStorage` thay vì context được ở ĐÂY vì thông điệp lỗi sinh ra **một
 * lần tại thời điểm lỗi**, rồi nằm im trong đối tượng `ApiError`. Nó không phải
 * vẽ lại khi người dùng đổi ngôn ngữ — mà kể cả có, thì lỗi cũ nói bằng thứ
 * tiếng lúc nó xảy ra cũng không sai.
 *
 * Đừng dùng hàm này trong component: ở đó React phải biết giá trị đã đổi.
 */
export function dichNgoaiReact(key: string, vars?: Record<string, string | number>): string {
  return dichKhoa(readLocale(), key, vars);
}

/**
 * Bản dịch cho **component lá** — `Field`, `Badge`, `SearchBox`: những thứ nằm
 * sâu trong cây và được dùng lại ở mọi nơi, kể cả trong test render trần.
 *
 * `useT()` ném lỗi khi thiếu `<I18nProvider>`, và với một màn hình thì ném là
 * đúng — thiếu provider ở đó là lỗi lắp ráp thật. Nhưng bắt một ô nhập chết vì
 * chuyện ấy thì đắt hơn cái nó bảo vệ: mọi test render ô nhập đều phải dựng
 * provider, cho một cái nhãn `aria-label` duy nhất.
 *
 * Không có provider thì rơi về `vi` — cùng nguyên tắc với khoá thiếu ở `en`.
 */
export function useTLa(): I18nValue['t'] {
  const ctx = useContext(I18nContext);
  const locale = ctx?.locale ?? 'vi';
  return useCallback(
    (key: string, vars?: Record<string, string | number>) => dichKhoa(locale, key, vars),
    [locale],
  );
}
